import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Lock, LogIn } from 'lucide-react';
import BackButton from '@/components/BackButton';
import cannafaxLogo from '@/assets/cannafax-logo-clean.png';
import AppHeader from '@/components/AppHeader';
import Footer from '@/components/Footer';

interface AccessDeniedProps {
  variant: 'unauthenticated' | 'unauthorized';
  message?: string;
  backTo?: string;
  backLabel?: string;
}

const AccessDenied = ({ variant, message, backTo = '/supplier-menus', backLabel = 'Back to Supplier Menus' }: AccessDeniedProps) => {
  const location = useLocation();
  const returnUrl = encodeURIComponent(location.pathname + location.search);

  if (variant === 'unauthenticated') {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader activeRoute="none" />
        <main className="flex-1 flex items-center justify-center px-4">
          <Card className="max-w-md w-full">
            <CardContent className="flex flex-col items-center justify-center py-12 px-6">
              <img src={cannafaxLogo} alt="cannafax.ai" className="h-8 mb-8" />
              <h2 className="text-xl font-medium mb-2 text-center">
                Sign in to view this content
              </h2>
              <p className="text-sm text-muted-foreground text-center mb-8">
                {message || 'This content requires an account. Sign in or create your free account to continue.'}
              </p>
              <Button asChild className="w-full mb-4">
                <a href={`/auth?returnUrl=${returnUrl}`}>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In to Continue
                </a>
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Don't have an account? Signing in with Google or Microsoft creates one automatically.
              </p>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // unauthorized variant
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader activeRoute="supplier-menus" />
      <main className="flex-1 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center justify-center py-12 px-6">
            <div className="circle-16 bg-muted flex items-center justify-center mb-6">
              <Lock className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-medium mb-2 text-center">
              You don't have access to this content
            </h2>
            <p className="text-sm text-muted-foreground text-center mb-8">
              {message || 'This menu is shared by invitation only. Contact the supplier to request access.'}
            </p>
            {/* Smart back navigation — goes to where user came from, falls back to provided backTo */}
            <BackButton fallbackPath={backTo} fallbackLabel={backLabel} />
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default AccessDenied;
